import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import MessageDialog from '../components/shared/MessageDialog';
import { useAuth } from '../context/AuthContext';
import { questionService } from '../services/questionService';

const parseTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getQuestionText = (question) => (
  question?.languages?.sv?.text
    || question?.question_sv
    || question?.question
    || question?.text
    || 'Okänd fråga'
);

const getValidationFailure = (question) => {
  const result = question?.aiValidationResult;
  if (!result) return false;
  if (result.valid === false) return true;
  if (result.isValid === false) return true;
  if (result.passed === false) return true;
  return false;
};

const resolveFlags = (question) => {
  const reported = question?.reported === true || (question?.reportCount || 0) > 0;
  const quarantined = question?.quarantined === true || question?.isExpired === true;
  const aiFailed = getValidationFailure(question);
  const manuallyRejected = question?.manuallyRejected === true;
  const manuallyApproved = question?.manuallyApproved === true;
  const needsReview = !manuallyApproved && !manuallyRejected && !aiFailed && !reported && !quarantined;
  return {
    reported,
    quarantined,
    aiFailed,
    manuallyRejected,
    manuallyApproved,
    needsReview
  };
};

const AdminModerationQueuePage = () => {
  const navigate = useNavigate();
  const { isSuperUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('queue');
  const [sort, setSort] = useState('newest');
  const [search, setSearch] = useState('');
  const [dialogConfig, setDialogConfig] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  useEffect(() => {
    if (!isSuperUser) {
      navigate('/');
      return;
    }
    let isActive = true;
    questionService.loadAllQuestions()
      .then((data) => {
        if (!isActive) return;
        setQuestions(data);
      })
      .catch((error) => {
        if (!isActive) return;
        setDialogConfig({
          isOpen: true,
          title: 'Kunde inte ladda frågor',
          message: error.message,
          type: 'error'
        });
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    const unsubscribe = questionService.subscribe((data) => {
      if (isActive) {
        setQuestions(data);
      }
    });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [isSuperUser, navigate]);

  const counters = useMemo(() => {
    const base = {
      reported: 0,
      quarantined: 0,
      aiFailed: 0,
      needsReview: 0,
      queue: 0
    };
    questions.forEach((question) => {
      const flags = resolveFlags(question);
      if (flags.reported) base.reported += 1;
      if (flags.quarantined) base.quarantined += 1;
      if (flags.aiFailed) base.aiFailed += 1;
      if (flags.needsReview) base.needsReview += 1;
      if (flags.reported || flags.quarantined || flags.aiFailed || flags.needsReview) base.queue += 1;
    });
    return base;
  }, [questions]);

  const filteredQuestions = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matchesQuery = (question) => {
      if (!query) return true;
      const haystack = [
        question?.id,
        getQuestionText(question),
        question?.category,
        ...(question?.categories || [])
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query);
    };

    const queueItems = questions.filter((question) => {
      const flags = resolveFlags(question);
      if (filter === 'reported') return flags.reported;
      if (filter === 'quarantined') return flags.quarantined;
      if (filter === 'ai-failed') return flags.aiFailed;
      if (filter === 'needs-review') return flags.needsReview;
      return flags.reported || flags.quarantined || flags.aiFailed || flags.needsReview;
    });

    const sorted = [...queueItems].sort((a, b) => {
      if (sort === 'reports') {
        return (b.reportCount || 0) - (a.reportCount || 0);
      }
      const aTime = parseTimestamp(a.createdAt || a.created_at) || 0;
      const bTime = parseTimestamp(b.createdAt || b.created_at) || 0;
      return sort === 'oldest' ? aTime - bTime : bTime - aTime;
    });

    return sorted.filter(matchesQuery);
  }, [filter, questions, search, sort]);

  const clearQuarantine = useCallback(async (question) => {
    try {
      await questionService.updateQuestionMeta(question.id, {
        quarantined: false,
        quarantinedAt: null,
        quarantineReason: null
      });
    } catch (error) {
      setDialogConfig({
        isOpen: true,
        title: 'Kunde inte ta bort karantän',
        message: error.message,
        type: 'error'
      });
    }
  }, []);

  const approveReported = useCallback(async (question) => {
    try {
      await questionService.approveReportedQuestion(question.id);
    } catch (error) {
      setDialogConfig({
        isOpen: true,
        title: 'Kunde inte godkänna rapport',
        message: error.message,
        type: 'error'
      });
    }
  }, []);

  const rejectReported = useCallback(async (question) => {
    try {
      await questionService.rejectReportedQuestion(question.id);
    } catch (error) {
      setDialogConfig({
        isOpen: true,
        title: 'Kunde inte underkänna rapport',
        message: error.message,
        type: 'error'
      });
    }
  }, []);

  const manualApprove = useCallback(async (question) => {
    try {
      if (question.reported) {
        await questionService.approveReportedQuestion(question.id);
      }
      if (question.quarantined || question.isExpired) {
        await clearQuarantine(question);
      }
      await questionService.markAsManuallyApproved(question.id);
    } catch (error) {
      setDialogConfig({
        isOpen: true,
        title: 'Kunde inte godkänna',
        message: error.message,
        type: 'error'
      });
    }
  }, [clearQuarantine]);

  const manualReject = useCallback(async (question) => {
    const reason = window.prompt('Ange anledning till underkännande:', 'Manuellt underkänd efter granskning');
    if (reason === null) return;
    try {
      if (question.reported) {
        await questionService.rejectReportedQuestion(question.id);
      } else {
        await questionService.markAsManuallyRejected(question.id, reason);
      }
    } catch (error) {
      setDialogConfig({
        isOpen: true,
        title: 'Kunde inte underkänna',
        message: error.message,
        type: 'error'
      });
    }
  }, []);

  if (!isSuperUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header title="Modereringskö" />
      <main className="mx-auto max-w-6xl px-4 pt-24 pb-10 space-y-6">
        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Innehåll som kräver åtgärd</h2>
              <p className="text-sm text-slate-400">
                Hantera rapporterade, karantänsatta och AI‑underkända frågor direkt här.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/questions')}
              className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:border-slate-500"
            >
              Öppna frågebank
            </button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 text-sm">
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <div className="text-xs text-slate-500">Kö totalt</div>
              <div className="text-lg font-semibold">{counters.queue}</div>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <div className="text-xs text-slate-500">Rapporterade</div>
              <div className="text-lg font-semibold text-amber-300">{counters.reported}</div>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <div className="text-xs text-slate-500">Karantän</div>
              <div className="text-lg font-semibold text-red-300">{counters.quarantined}</div>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <div className="text-xs text-slate-500">AI‑underkända</div>
              <div className="text-lg font-semibold text-orange-300">{counters.aiFailed}</div>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <div className="text-xs text-slate-500">Behöver granskning</div>
              <div className="text-lg font-semibold text-cyan-200">{counters.needsReview}</div>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm text-slate-400">Filter</div>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="queue">Allt i kö</option>
              <option value="reported">Rapporterade</option>
              <option value="quarantined">Karantän</option>
              <option value="ai-failed">AI‑underkända</option>
              <option value="needs-review">Behöver granskning</option>
            </select>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="newest">Nyast först</option>
              <option value="oldest">Äldst först</option>
              <option value="reports">Flest rapporter</option>
            </select>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="flex-1 min-w-[180px] rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
              placeholder="Sök på fråga, kategori eller id"
            />
          </div>
        </section>

        {loading ? (
          <div className="text-center text-slate-400">Laddar modereringskö...</div>
        ) : filteredQuestions.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 text-center text-slate-400">
            Inga frågor att moderera just nu.
          </div>
        ) : (
          <div className="space-y-4">
            {filteredQuestions.map((question) => {
              const flags = resolveFlags(question);
              const reports = Array.isArray(question.reports) ? question.reports : [];
              const createdAt = parseTimestamp(question.createdAt || question.created_at);
              const bestBeforeAt = parseTimestamp(question.bestBeforeAt || question.best_before_at);
              return (
                <div key={question.id} className="rounded-xl border border-slate-800 bg-slate-900/70 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm text-slate-400">Fråga</div>
                      <div className="text-lg font-semibold text-white">{getQuestionText(question)}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        ID: {question.id}
                        {createdAt && <span className="ml-2">Skapad: {new Date(createdAt).toLocaleString('sv-SE')}</span>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {flags.reported && <span className="rounded-full bg-amber-500/20 px-2 py-1 text-amber-200">Rapporterad</span>}
                      {flags.quarantined && <span className="rounded-full bg-red-500/20 px-2 py-1 text-red-200">Karantän</span>}
                      {flags.aiFailed && <span className="rounded-full bg-orange-500/20 px-2 py-1 text-orange-200">AI‑underkänd</span>}
                      {flags.needsReview && <span className="rounded-full bg-cyan-500/20 px-2 py-1 text-cyan-200">Behöver granskning</span>}
                      {question.manuallyApproved && <span className="rounded-full bg-emerald-500/20 px-2 py-1 text-emerald-200">Manuellt godkänd</span>}
                      {question.manuallyRejected && <span className="rounded-full bg-red-500/20 px-2 py-1 text-red-200">Manuellt underkänd</span>}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-300">
                    <div>
                      Kategorier: {(
                        Array.isArray(question.categories)
                          ? question.categories
                          : question.category
                            ? [question.category]
                            : []
                      ).filter(Boolean).join(', ') || '—'}
                    </div>
                    <div>Åldersgrupper: {(question.ageGroups || []).join(', ') || '—'}</div>
                    <div>Målgrupp: {question.targetAudience || '—'}</div>
                    <div>Rapporter: {question.reportCount || reports.length || 0}</div>
                    {bestBeforeAt && (
                      <div>Bäst före: {new Date(bestBeforeAt).toLocaleDateString('sv-SE')}</div>
                    )}
                    {question.quarantineReason && (
                      <div>Karantän: {question.quarantineReason}</div>
                    )}
                  </div>

                  {reports.length > 0 && (
                    <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs text-slate-300 space-y-2">
                      <div className="text-slate-400 font-semibold">Rapporter</div>
                      {reports.map((report, index) => (
                        <div key={`${question.id}-report-${index}`} className="flex flex-wrap gap-2">
                          <span>• {report.reason || 'Ingen anledning angiven'}</span>
                          <span className="text-slate-500">({report.reportedBy || 'Anonym'})</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {flags.reported && (
                      <>
                        <button
                          type="button"
                          onClick={() => approveReported(question)}
                          className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-emerald-400"
                        >
                          Godkänn rapport
                        </button>
                        <button
                          type="button"
                          onClick={() => rejectReported(question)}
                          className="rounded-lg bg-red-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-400"
                        >
                          Underkänn rapport
                        </button>
                      </>
                    )}
                    {(flags.quarantined || question.quarantineReason) && (
                      <button
                        type="button"
                        onClick={() => clearQuarantine(question)}
                        className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:border-slate-500"
                      >
                        Ta bort karantän
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => manualApprove(question)}
                      className="rounded-lg border border-emerald-400 px-3 py-1.5 text-xs text-emerald-200 hover:border-emerald-300"
                    >
                      Manuellt godkänn
                    </button>
                    <button
                      type="button"
                      onClick={() => manualReject(question)}
                      className="rounded-lg border border-red-400 px-3 py-1.5 text-xs text-red-200 hover:border-red-300"
                    >
                      Manuellt underkänn
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <MessageDialog
        isOpen={dialogConfig.isOpen}
        onClose={() => setDialogConfig((prev) => ({ ...prev, isOpen: false }))}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
      />
    </div>
  );
};

export default AdminModerationQueuePage;
